import { ArrowUpRight, Dna, GraduationCap, Leaf, Mountain } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import { scrollToSection } from "./Navbar";

const SEEDLING_IMG =
  "https://images.unsplash.com/photo-1605274792202-333d16abe028?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxib3RhbmljYWwlMjByZXNlYXJjaCUyMGxhYiUyMHNjaWVudGlzdCUyMHBsYW50JTIwZXh0cmFjdCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzkzNzQzMDYyfDA&ixlib=rb-4.1.0&q=85";
const FLASK_IMG =
  "https://images.unsplash.com/photo-1584725272786-8d98c7793f7f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxib3RhbmljYWwlMjByZXNlYXJjaCUyMGxhYiUyMHNjaWVudGlzdCUyMHBsYW50JTIwZXh0cmFjdCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzkzNzQzMDYyfDA&ixlib=rb-4.1.0&q=85";
const SAND_IMG =
  "https://images.unsplash.com/photo-1545486332-ae6c3bf92201?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY6NzV8MHwxfHNlYXJjaHwzfHxibGFjayUyMHNhbmQlMjBiZWFjaCUyMGRhcmslMjB0ZXh0dXJlJTIwYmFzYWx0JTIwcm9ja3xlbnwwfHx8fDE3OTM3NDMwNjJ8MA&ixlib=rb-4.1.0&q=85";

const CardShell = ({ children, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`group relative overflow-hidden rounded-2xl border border-[#FBF9F4]/10 bg-[#1A1F1A] transition-colors duration-500 hover:border-[#D4A359]/40 ${className}`}
  >
    {children}
  </div>
);

export const WhatWeDo = () => (
  <section id="what-we-do" className="relative bg-[#121512] py-28 sm:py-40" data-testid="what-we-do-section">
    <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14 sm:mb-20">
        <div>
          <FadeUp>
            <Eyebrow dark>02 — What We Do</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif-brand text-4xl sm:text-5xl lg:text-6xl text-[#FBF9F4] leading-[1.05] tracking-tight mt-6 max-w-2xl">
              Four pillars, <span className="italic text-[#D4A359]">one wharenui.</span>
            </h2>
          </FadeUp>
        </div>
        <FadeUp delay={0.2}>
          <p className="text-sm sm:text-base text-[#D1D8D2]/70 max-w-xs leading-relaxed">
            From genetics to gate — a controlled, research-led path to licensed commercial cultivation.
          </p>
        </FadeUp>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <FadeUp className="lg:col-span-8" delay={0}>
          <CardShell className="h-full min-h-[380px] lg:min-h-[440px]" testid="bento-card-cultivation">
            <img
              src={SEEDLING_IMG}
              alt="Young botanical seedling under controlled cultivation light"
              className="absolute inset-0 w-full h-full object-cover opacity-55 transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#121512] via-[#121512]/30 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 sm:p-10">
              <Leaf className="w-6 h-6 text-[#D4A359] mb-5" />
              <h3 className="font-serif-brand text-3xl sm:text-4xl text-[#FBF9F4]">GACP / GMP Cultivation</h3>
              <p className="mt-3 max-w-md text-sm sm:text-base text-[#D1D8D2]/80 leading-relaxed">
                High-quality, New Zealand-grown medicinal cannabis for licensed manufacturers and
                distributors — cultivated to pharmaceutical standards, tested before sale.
              </p>
            </div>
          </CardShell>
        </FadeUp>

        <FadeUp className="lg:col-span-4" delay={0.1}>
          <CardShell className="h-full min-h-[380px] lg:min-h-[440px]" testid="bento-card-research">
            <img
              src={FLASK_IMG}
              alt="Botanical research sample in a laboratory flask"
              className="absolute inset-0 w-full h-full object-cover opacity-40 transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#121512] via-[#121512]/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <Dna className="w-6 h-6 text-[#D4A359] mb-5" />
              <h3 className="font-serif-brand text-2xl sm:text-3xl text-[#FBF9F4]">Genetics &amp; Cultivar Research</h3>
              <p className="mt-3 text-sm text-[#D1D8D2]/80 leading-relaxed">
                Distinctive, well-documented cultivars suited to Kāwhia's volcanic soils —
                developed and proven through site trials.
              </p>
            </div>
          </CardShell>
        </FadeUp>

        <FadeUp className="lg:col-span-4" delay={0}>
          <CardShell className="h-full min-h-[300px]" testid="bento-card-training">
            <div className="relative h-full flex flex-col justify-between p-8">
              <GraduationCap className="w-6 h-6 text-[#D4A359]" />
              <div className="mt-14">
                <h3 className="font-serif-brand text-2xl sm:text-3xl text-[#FBF9F4]">Training &amp; Employment</h3>
                <p className="mt-3 text-sm text-[#D1D8D2]/80 leading-relaxed">
                  Real career pathways in biosciences and biotechnology for rangatahi and whānau —
                  growing people alongside plants.
                </p>
              </div>
            </div>
          </CardShell>
        </FadeUp>

        <FadeUp className="lg:col-span-4" delay={0.1}>
          <CardShell className="h-full min-h-[300px]" testid="bento-card-provenance">
            <img
              src={SAND_IMG}
              alt="Dark volcanic black-sand texture of the Kāwhia coast"
              className="absolute inset-0 w-full h-full object-cover opacity-45 transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#121512] via-[#121512]/35 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <Mountain className="w-6 h-6 text-[#D4A359] mb-5" />
              <h3 className="font-serif-brand text-2xl sm:text-3xl text-[#FBF9F4]">Kāwhia Provenance</h3>
              <p className="mt-3 text-sm text-[#D1D8D2]/80 leading-relaxed">
                Volcanic soils, black iron sands and coastal air — provenance backed by data, not just story.
              </p>
            </div>
          </CardShell>
        </FadeUp>

        <FadeUp className="lg:col-span-4" delay={0.2}>
          <button
            onClick={() => scrollToSection("#contact")}
            data-testid="bento-card-partner-cta"
            className="group relative w-full h-full min-h-[300px] overflow-hidden rounded-2xl bg-[#D4A359] p-8 text-left transition-colors duration-500 hover:bg-[#FBF9F4] flex flex-col justify-between"
          >
            <ArrowUpRight className="w-7 h-7 text-[#121512] transition-transform duration-500 group-hover:translate-x-1.5 group-hover:-translate-y-1.5" />
            <div>
              <h3 className="font-serif-brand text-2xl sm:text-3xl text-[#121512] leading-tight">
                Supply or fund
                <br />
                this mahi.
              </h3>
              <p className="mt-3 text-sm text-[#121512]/70 leading-relaxed">
                We're talking with funders, equipment suppliers and research partners now.
              </p>
            </div>
          </button>
        </FadeUp>
      </div>
    </div>
  </section>
);
